import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Stagger, StaggerItem } from "@/components/Reveal";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/site";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.listPosts(), api.categories()])
      .then(([p, c]) => {
        setPosts(p.data);
        setCats(["All", ...c.data]);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = active === "All" ? posts : posts.filter((p) => p.category === active);

  return (
    <>
      <Seo
        title="Local Guide & Blog | Things to Do in Pittsburg, KS"
        description="Insider guides to Pittsburg, Kansas from Regency Inn & Suites — best restaurants, things to do, family and business travel tips, extended-stay advice and seasonal guides."
        path="/local-guide"
        image={IMAGES.landscape}
        schema={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Local Guide", path: "/local-guide" }])]}
      />
      <PageHero
        eyebrow="Local Guide"
        title="Stories & guides from Pittsburg."
        subtitle="Local know-how to help you eat well, explore more and make the most of your stay in southeast Kansas."
        image={IMAGES.landscape}
        crumbs={[{ name: "Home", path: "/" }, { name: "Local Guide", path: "/local-guide" }]}
      />

      <section className="bg-bone py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-3 mb-12">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`blog-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                className={`rounded-full px-5 py-2 text-sm transition-colors ${
                  active === c ? "bg-forest text-bone" : "border border-stone/25 text-stone hover:border-amber hover:text-amber-deep"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-stone">Loading guides…</p>
          ) : (
            <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((p) => (
                <StaggerItem key={p.id}>
                  <Link to={`/local-guide/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block h-full">
                    <div className="img-zoom aspect-[3/2] rounded-md">
                      <img src={p.cover_image} alt={p.title} loading="lazy" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/assets/hotel/g6.jpg"; }} className="w-full h-full object-cover" />
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-amber-deep">
                        <span>{p.category}</span>
                        <span className="text-stone">· {p.read_time} min read</span>
                      </div>
                      <h2 className="font-serif text-2xl text-forest mt-2 group-hover:text-amber-deep transition-colors leading-snug">
                        {p.title}
                      </h2>
                      <p className="text-stone mt-2 text-[15px] leading-relaxed line-clamp-3">{p.excerpt}</p>
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>
          )}
          {!loading && filtered.length === 0 && <p className="text-stone">No guides in this category yet.</p>}
        </div>
      </section>
    </>
  );
}
